import type React from "react";
import { useEffect, useState } from "react";

interface FallbackImageProps {
  playerUrl?: string | null;
  className?: string;
  alt?: string;
  variant?: "banner" | "avatar";
}

export const getUnavatarUrl = (playerUrl: string | null | undefined): string | null => {
  if (!playerUrl) return null;
  const trimmed = playerUrl.trim();
  if (!trimmed) return null;

  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    try {
      parsed = new URL(`https://${trimmed}`);
    } catch {
      return null;
    }
  }

  const hostname = parsed.hostname.toLowerCase();

  // 1. SoundCloud: https://unavatar.io/soundcloud/:username
  if (hostname.includes("soundcloud.com")) {
    const parts = parsed.pathname.split("/").filter(Boolean);
    if (parts.length > 0) {
      const username = parts[0];
      if (username !== "player" && username !== "tracks") {
        return `https://unavatar.io/soundcloud/${username}`;
      }
    }
  }

  // 2. Spotify: https://unavatar.io/spotify/:type/:id
  if (hostname.includes("spotify.com")) {
    const parts = parsed.pathname.split("/").filter(Boolean);
    if (parts.length >= 2) {
      const type = parts[0]; // artist, album, track, playlist, user
      const id = parts[1];
      if (["artist", "album", "track", "playlist", "user"].includes(type)) {
        return `https://unavatar.io/spotify/${type}:${id}`;
      }
    }
  }

  // 3. YouTube: https://unavatar.io/youtube/:username
  if (hostname.includes("youtube.com") || hostname.includes("youtu.be")) {
    if (parsed.pathname.includes("/@")) {
      const username = parsed.pathname
        .split("/")
        .find((p) => p.startsWith("@"))
        ?.substring(1);
      if (username) {
        return `https://unavatar.io/youtube/${username}`;
      }
    }
  }

  // 4. Fallback: domain logo via unavatar
  const domain = hostname.replace(/^www\./, "");
  if (domain) {
    return `https://unavatar.io/${domain}`;
  }

  return null;
};

export const FallbackImage: React.FC<FallbackImageProps> = ({
  playerUrl,
  className = "",
  alt = "Fallback Image",
  variant = "banner",
}) => {
  const [error, setError] = useState<any | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  if (error) {
    console.warn("error while loading image", error);
  }

  const unavatarUrl = getUnavatarUrl(playerUrl);

  // Reset states if playerUrl changes
  useEffect(() => {
    setError(null);
    setIsLoaded(false);
  }, [playerUrl]);

  if (variant === "banner") {
    const showImage = unavatarUrl && isLoaded && !error;
    return (
      <>
        {unavatarUrl && !isLoaded && !error && (
          <img
            src={unavatarUrl}
            alt=""
            className="hidden"
            onLoad={() => setIsLoaded(true)}
            onError={(e) => setError(e)}
          />
        )}
        {showImage ? (
          <div
            className={`absolute inset-0 w-full h-full bg-cover bg-no-repeat animate-bg-scroll z-0 ${className}`}
            style={{
              backgroundImage: `url(${unavatarUrl})`,
            }}
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-tr from-[#222222] via-[#333333] to-main/10 opacity-70 z-0" />
        )}
      </>
    );
  }

  // variant === "avatar"
  if (unavatarUrl && !error) {
    return (
      <img
        src={unavatarUrl}
        alt={alt}
        onError={(e) => setError(e)}
        className={`w-full h-full object-cover ${className}`}
      />
    );
  }

  return (
    <div className="w-full h-full flex items-center justify-center text-muted">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-5 w-5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        aria-hidden="true"
        role="img"
      >
        <title>Előadó ikon</title>
        <desc>Alapértelmezett előadói avatar</desc>
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
        />
      </svg>
    </div>
  );
};

export default FallbackImage;
